import React, { useEffect, useRef, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { CodeIcon, Link2Icon } from '@radix-ui/react-icons';
import api from '../../services/api';
import Button from '../../components/common/Button/Button';
import styles from './AddLeadCampaign.module.css';
import {
    MAX_IMAGES,
    MAX_CLOSE_DELAY_MS,
    hasGuaranteedDismissal,
    FEED_PLACEMENTS,
    CREATIVE_TYPES,
    TARGET_ACTIONS,
    TARGET_GROUPS,
    PLACEMENT_META,
    buildPlacementGroups,
} from './campaignOptions';
import { initialFormData, titleCase, buildFormState } from './campaignFormUtils';
import CreativeSection from './CreativeSection';
import MediaLibraryPicker from './MediaLibraryPicker';
import DeleteAssetDialog from './DeleteAssetDialog';

const AddLeadCampaign = ({ campaignId, initialData, onSubmitted, onCancel }) => {
    const id = campaignId;
    const isEditing = Boolean(id);
    // When the list already has this campaign's full row, seed the form from it
    // immediately — no fetch, no loading spinner. Computed once.
    const [seed] = useState(() => (initialData ? buildFormState(initialData) : null));
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [fetchingDetail, setFetchingDetail] = useState(isEditing && !seed);
    const [formData, setFormData] = useState(seed ? seed.formData : initialFormData);
    // Input modes so the user clearly sees what they're entering.
    const [creativeMode, setCreativeMode] = useState(seed ? seed.creativeMode : 'image'); // 'image' | 'html'
    const [actionMode, setActionMode] = useState(seed ? seed.actionMode : 'url');          // 'url' | 'action'
    // Image mode is now a list of library assets, each with its own redirect:
    // { asset_id, file_url, alt_text, redirect_url }.
    const [creativeItems, setCreativeItems] = useState(seed ? seed.creativeItems : []);
    // The media library (for the "choose from library" picker).
    const [library, setLibrary] = useState([]);
    const [libraryOpen, setLibraryOpen] = useState(false);
    const [libraryLoading, setLibraryLoading] = useState(false);
    // Asset pending deletion (drives the confirm dialog) + its in-flight flag.
    const [assetToDelete, setAssetToDelete] = useState(null);
    const [deletingAsset, setDeletingAsset] = useState(false);
    // Inline external-link entry: which kind of link input is open ('video' for a
    // direct file URL, 'youtube' for a YouTube/Vimeo embed), and its current text.
    const [linkKind, setLinkKind] = useState(null);
    const [linkValue, setLinkValue] = useState('');
    // Snapshot of the saved state, so the submit button enables only on a real
    // change. Seeded from the loaded record (list row or fetch), else blank form.
    const baselineRef = useRef(seed ? seed.baseline : JSON.stringify({ fd: initialFormData, items: [] }));

    useEffect(() => {
        // Already seeded from the list row (or creating) → nothing to fetch.
        if (!isEditing || seed) return;

        setFetchingDetail(true);
        api.get(`/lead-campaigns/${id}`)
            .then((response) => {
                const data = response?.data ?? response?.result ?? response ?? {};
                if (!data || (!data.id && String(data.id) !== '0')) {
                    toast.error("Campaign not found");
                    if (onCancel) onCancel();
                    return;
                }
                const s = buildFormState(data);
                setCreativeItems(s.creativeItems);
                setFormData(s.formData);
                baselineRef.current = s.baseline;
                setCreativeMode(s.creativeMode);
                setActionMode(s.actionMode);
            })
            .catch((err) => {
                console.error("Failed to load campaign details", err);
                toast.error(err?.data?.message || err?.message || "Failed to load campaign details");
            })
            .finally(() => setFetchingDetail(false));
    }, [id, isEditing]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // The form has unsaved changes when the current state differs from the
    // baseline (blank on create, the loaded record on edit). Drives the button.
    const isDirty = JSON.stringify({ fd: formData, items: creativeItems }) !== baselineRef.current;

    // Uploads a new image, registering it in the media library, then adds it to
    // this campaign's image list. The bytes live under /uploads; the campaign
    // only references the returned asset id + url.
    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (creativeItems.length >= MAX_IMAGES) {
            toast.error(`Up to ${MAX_IMAGES} images per campaign.`);
            e.target.value = "";
            return;
        }

        const fd = new FormData();
        fd.append("image", file);
        setUploading(true);
        try {
            const res = await api.post("/lead-campaigns/assets", fd);
            const asset = res?.data;
            if (!asset || !asset.id) {
                toast.error("Upload failed — no asset returned");
                return;
            }
            addItem(asset);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(err?.message || "Image upload failed");
        } finally {
            setUploading(false);
            e.target.value = ""; // allow re-picking the same file
        }
    };

    // Uploads a video to the media library (its own endpoint with a video-only
    // whitelist and larger size cap) and appends it as a creative item. The app
    // detects the video by its file extension and plays it; per-item redirect
    // works exactly like images.
    const handleVideoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (creativeItems.length >= MAX_IMAGES) {
            toast.error(`Up to ${MAX_IMAGES} items per campaign.`);
            e.target.value = "";
            return;
        }

        const fd = new FormData();
        fd.append("video", file);
        setUploading(true);
        try {
            const res = await api.post("/lead-campaigns/assets/video", fd);
            const asset = res?.data;
            if (!asset || !asset.id) {
                toast.error("Upload failed — no asset returned");
                return;
            }
            addItem(asset);
            toast.success("Video uploaded");
        } catch (err) {
            toast.error(err?.message || "Video upload failed");
        } finally {
            setUploading(false);
            e.target.value = ""; // allow re-picking the same file
        }
    };

    // Registers an external link (direct video file, or YouTube/Vimeo embed) as a
    // library asset via the backend validator, then adds it as a creative item.
    // No file leaves the browser; the backend is the source of truth for what's
    // an acceptable URL, so nothing unsafe can be stored.
    const submitLink = async () => {
        const url = linkValue.trim();
        if (!url) return;
        if (creativeItems.length >= MAX_IMAGES) {
            toast.error(`Up to ${MAX_IMAGES} items per campaign.`);
            return;
        }
        setUploading(true);
        try {
            const res = await api.post('/lead-campaigns/assets/link', { url, kind: linkKind });
            const asset = res?.data;
            if (!asset || !asset.id) {
                toast.error('Could not add link');
                return;
            }
            addItem(asset);
            toast.success('Link added');
            setLinkValue('');
            setLinkKind(null);
        } catch (err) {
            toast.error(err?.message || 'Invalid link');
        } finally {
            setUploading(false);
        }
    };

    // Appends a library asset to this campaign's image list (with an empty
    // redirect, meaning it falls back to the campaign's Target Action).
    const addItem = (asset) => {
        setCreativeItems((prev) => {
            if (prev.length >= MAX_IMAGES) return prev;
            return [...prev, {
                asset_id: asset.id,
                file_url: asset.file_url,
                alt_text: asset.alt_text || '',
                redirect_url: '',
            }];
        });
    };

    const removeItem = (index) => {
        setCreativeItems((prev) => prev.filter((_, i) => i !== index));
    };

    const setItemRedirect = (index, value) => {
        setCreativeItems((prev) => prev.map((it, i) => (i === index ? { ...it, redirect_url: value } : it)));
    };

    // Opens the media-library picker, loading the asset list on first open.
    const openLibrary = async () => {
        setLibraryOpen(true);
        setLibraryLoading(true);
        try {
            const res = await api.get("/lead-campaigns/assets");
            setLibrary(Array.isArray(res?.data) ? res.data : []);
        } catch (err) {
            toast.error(err?.message || "Failed to load library");
        } finally {
            setLibraryLoading(false);
        }
    };

    // Removes an image from the library so it stops cluttering the picker. The
    // backend refuses (409) when the image is still used by a campaign, so a live
    // creative can never be broken by a delete here.
    const confirmDeleteAsset = async () => {
        if (!assetToDelete) return;
        setDeletingAsset(true);
        try {
            await api.delete(`/lead-campaigns/assets/${assetToDelete.id}`);
            setLibrary((prev) => prev.filter((x) => x.id !== assetToDelete.id));
            toast.success('Deleted from library');
            setAssetToDelete(null);
        } catch (err) {
            if (err?.status === 409) {
                const used = (err?.data?.data || []).map((c) => c.placement_name).filter(Boolean);
                toast.error(used.length
                    ? `In use by: ${used.join(', ')}. Remove it there first.`
                    : 'Image is in use by a campaign. Remove it there first.');
                setAssetToDelete(null);
            } else {
                toast.error(err?.message || 'Delete failed');
            }
        } finally {
            setDeletingAsset(false);
        }
    };

    // Adds a picked library asset (skips duplicates already on this campaign).
    const pickFromLibrary = (asset) => {
        if (creativeItems.length >= MAX_IMAGES) {
            toast.error(`Up to ${MAX_IMAGES} images per campaign.`);
            return;
        }
        if (creativeItems.some((it) => it.asset_id === asset.id)) {
            toast.info("That image is already added.");
            return;
        }
        addItem(asset);
        setLibraryOpen(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.placement_name) {
            toast.error("Placement name is required.");
            return;
        }
        const isHtmlMode = creativeMode === 'html';
        if (isHtmlMode && !formData.creative_html.trim()) {
            toast.error("Creative HTML is required.");
            return;
        }
        if (!isHtmlMode && creativeItems.length === 0) {
            toast.error("Add at least one image.");
            return;
        }
        // Both timings are converted ONCE here and reused in the payload below, so
        // a guard can never test a different value than the one actually sent.
        const durationMs = formData.duration_value !== '' && Number(formData.duration_value) > 0
            ? Math.round(Number(formData.duration_value) * (formData.duration_unit === 'minutes' ? 60000 : 1000))
            : null;
        const closeDelayMs = formData.close_delay_value !== '' && Number(formData.close_delay_value) > 0
            ? Math.round(Number(formData.close_delay_value) * (formData.close_delay_unit === 'minutes' ? 60000 : 1000))
            : 0;
        // The cap guards against a unit mistake and so applies to every type.
        if (closeDelayMs > MAX_CLOSE_DELAY_MS) {
            toast.error(`Close button delay cannot exceed ${MAX_CLOSE_DELAY_MS / 60000} minutes.`);
            return;
        }
        // The remaining checks exist only for creatives the user cannot otherwise
        // escape — see hasGuaranteedDismissal. Applying them to popups too would
        // reject configurations the app already handles safely.
        if (!hasGuaranteedDismissal(formData.creative_type)) {
            // A delay that outlives the auto-hide would withhold the button until
            // after the creative has already removed itself — the setting would
            // silently do nothing.
            if (durationMs != null && closeDelayMs >= durationMs) {
                toast.error("Close button delay must be shorter than the auto-hide duration.");
                return;
            }
        }

        setLoading(true);
        try {
            const payload = {
                placement_name: formData.placement_name,
                creative_type: formData.creative_type,
                // HTML mode sends markup; image mode sends the linked assets and
                // lets the backend mirror their URLs into creative_html.
                creative_html: isHtmlMode ? formData.creative_html : '',
                creative_items: isHtmlMode ? [] : creativeItems.map((it) => ({
                    asset_id: it.asset_id,
                    redirect_url: it.redirect_url.trim() || null,
                })),
                target_action: formData.target_action,
                target_user_group: formData.target_user_group || 'all',
                priority: formData.priority != null && formData.priority !== '' ? Number(formData.priority) : null,
                sponsor_name: formData.sponsor_name || null,
                notes: formData.notes || null,
                activation_timestamp: formData.activation_timestamp || null,
                deactivation_timestamp: formData.deactivation_timestamp || null,
                status: formData.status,
                // Already converted from the entered value + unit above; null = stays.
                display_duration_ms: durationMs,
                // In-feed position/repeat — only sent for feed placements.
                feed_position: FEED_PLACEMENTS.includes(formData.placement_name) && formData.feed_position !== '' && Number(formData.feed_position) >= 0
                    ? Math.trunc(Number(formData.feed_position))
                    : null,
                feed_repeat: FEED_PLACEMENTS.includes(formData.placement_name) && formData.feed_repeat !== '' && Number(formData.feed_repeat) > 0
                    ? Math.trunc(Number(formData.feed_repeat))
                    : null,
                show_close_button: formData.show_close_button,
                // Already converted from the entered value + unit above; 0 = immediate.
                close_button_delay_ms: closeDelayMs,
            };

            if (isEditing) {
                await api.put(`/lead-campaigns/${id}`, payload);
                toast.success("Campaign updated successfully!");
            } else {
                await api.post('/lead-campaigns', payload);
                toast.success("Campaign created successfully!");
            }

            setTimeout(() => {
                if (onSubmitted) onSubmitted();
            }, 800);
        } catch (error) {
            console.error("Error saving campaign:", error);
            const errMsg = error?.data?.message || error?.message || "Failed to save campaign";
            toast.error(errMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <ToastContainer position="bottom-right" autoClose={2000} />

            <div className={styles.card}>
                {fetchingDetail ? (
                    <div className={styles.loadingState}>Loading campaign details...</div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className={styles.formGrid}>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Campaign ID</label>
                                <input
                                    type="text"
                                    name="id"
                                    value={formData.id}
                                    placeholder="Auto-generated after saving"
                                    className={styles.input}
                                    disabled
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Placement <span className={styles.required}>*</span></label>
                                <select
                                    name="placement_name"
                                    value={formData.placement_name}
                                    onChange={handleChange}
                                    className={`${styles.input} ${styles.placementSelect}`}
                                    required
                                >
                                    <option value="" disabled>Select where this ad appears…</option>
                                    {buildPlacementGroups(formData.creative_type, formData.placement_name).map((grp) => (
                                        <optgroup key={grp.group} label={grp.group}>
                                            {grp.items.map((slug) => (
                                                <option key={slug} value={slug}>
                                                    {grp.custom ? `Current: ${slug}` : `•  ${PLACEMENT_META[slug]?.label || titleCase(slug)}`}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                                <span className={styles.hint}>
                                    {PLACEMENT_META[formData.placement_name]?.desc
                                        || (formData.placement_name ? 'Custom placement — shows only if the app has this slot.' : 'Pick the tab and the exact spot where this ad should show.')}
                                </span>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Creative Type <span className={styles.required}>*</span></label>
                                <select name="creative_type" value={formData.creative_type} onChange={handleChange} className={styles.input}>
                                    {CREATIVE_TYPES.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <div className={styles.labelRow}>
                                    <label className={styles.label}>Target Action</label>
                                    <div className={styles.modeToggle}>
                                        <button type="button"
                                            className={actionMode === 'url' ? styles.modeBtnActive : styles.modeBtn}
                                            onClick={() => setActionMode('url')}>
                                            <Link2Icon /> URL
                                        </button>
                                        <button type="button"
                                            className={actionMode === 'action' ? styles.modeBtnActive : styles.modeBtn}
                                            onClick={() => setActionMode('action')}>
                                            <CodeIcon /> Action
                                        </button>
                                    </div>
                                </div>
                                {actionMode === 'url' ? (
                                    <input
                                        type="text"
                                        name="target_action"
                                        value={formData.target_action}
                                        onChange={handleChange}
                                        placeholder="https://…  (opens inside the app)"
                                        className={styles.input}
                                    />
                                ) : (
                                    <select
                                        name="target_action"
                                        value={formData.target_action}
                                        onChange={handleChange}
                                        className={styles.input}
                                    >
                                        <option value="">— select an action —</option>
                                        {TARGET_ACTIONS.map((a) => <option key={a} value={a}>{titleCase(a)}</option>)}
                                    </select>
                                )}
                                <span className={styles.hint}>
                                    {actionMode === 'url'
                                        ? 'A URL opens inside the app with a back button.'
                                        : '"open_contact_form" opens the app\'s contact form.'}
                                </span>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Target User Group</label>
                                <select
                                    name="target_user_group"
                                    value={formData.target_user_group}
                                    onChange={handleChange}
                                    className={styles.input}
                                >
                                    {TARGET_GROUPS.map((g) => (
                                        <option key={g.value} value={g.value}>{g.label}</option>
                                    ))}
                                </select>
                                <span className={styles.hint}>Who sees this creative. "All users" = everyone; a tier shows only to that group (never to others).</span>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Priority</label>
                                <input type="number" name="priority" value={formData.priority} onChange={handleChange} min="0" className={styles.input} />
                                <span className={styles.hint}>Inline slots: order (lower shows first). Popups: the app-open number it appears on (e.g. 3 = user's 3rd open).</span>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Sponsor Name</label>
                                <input type="text" name="sponsor_name" value={formData.sponsor_name} onChange={handleChange} placeholder="Blank if in-house" className={styles.input} />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Status <span className={styles.required}>*</span></label>
                                <div className={styles.segmentContainer}>
                                    <button 
                                        type="button" 
                                        className={`${styles.segmentBtn} ${formData.status === 'active' ? styles.segmentActive : ''}`}
                                        onClick={() => setFormData(prev => ({ ...prev, status: 'active' }))}
                                    >
                                        Active
                                    </button>
                                    <button 
                                        type="button" 
                                        className={`${styles.segmentBtn} ${formData.status === 'inactive' ? styles.segmentInactive : ''}`}
                                        onClick={() => setFormData(prev => ({ ...prev, status: 'inactive' }))}
                                    >
                                        Inactive
                                    </button>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Active From</label>
                                <input type="datetime-local" name="activation_timestamp" value={formData.activation_timestamp} onChange={handleChange} className={styles.input} />
                                <span className={styles.hint}>Can be future-dated; goes live automatically.</span>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Active To</label>
                                <input type="datetime-local" name="deactivation_timestamp" value={formData.deactivation_timestamp} onChange={handleChange} className={styles.input} />
                                <span className={styles.hint}>Auto turns off after this time.</span>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Auto-hide after</label>
                                <div className={styles.durationRow}>
                                    <input
                                        type="number"
                                        min="0"
                                        name="duration_value"
                                        value={formData.duration_value}
                                        onChange={handleChange}
                                        placeholder="e.g. 30"
                                        className={styles.input}
                                    />
                                    <select name="duration_unit" value={formData.duration_unit} onChange={handleChange} className={styles.input}>
                                        <option value="seconds">seconds</option>
                                        <option value="minutes">minutes</option>
                                    </select>
                                </div>
                                <span className={styles.hint}>Banner auto-closes after this. Leave empty to keep it until the user closes it.</span>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Show Close Button</label>
                                <div className={styles.segmentContainer}>
                                    <button
                                        type="button"
                                        className={`${styles.segmentBtn} ${formData.show_close_button ? styles.segmentActive : ''}`}
                                        onClick={() => setFormData((prev) => ({ ...prev, show_close_button: true }))}
                                    >
                                        Shown
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.segmentBtn} ${!formData.show_close_button ? styles.segmentInactive : ''}`}
                                        onClick={() => setFormData((prev) => ({ ...prev, show_close_button: false }))}
                                    >
                                        Hidden
                                    </button>
                                </div>
                            </div>

                            {formData.show_close_button && (
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Show close button after</label>
                                    <div className={styles.durationRow}>
                                        <input
                                            type="number"
                                            min="0"
                                            name="close_delay_value"
                                            value={formData.close_delay_value}
                                            onChange={handleChange}
                                            placeholder="e.g. 5"
                                            className={styles.input}
                                        />
                                        <select name="close_delay_unit" value={formData.close_delay_unit} onChange={handleChange} className={styles.input}>
                                            <option value="seconds">seconds</option>
                                            <option value="minutes">minutes</option>
                                        </select>
                                    </div>
                                    <span className={styles.hint}>Delay before the close button appears. Leave blank or 0 to show immediately.</span>
                                </div>
                            )}

                            {FEED_PLACEMENTS.includes(formData.placement_name) && (
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>In-feed position</label>
                                    <div className={styles.durationRow}>
                                        <input
                                            type="number"
                                            min="0"
                                            name="feed_position"
                                            value={formData.feed_position}
                                            onChange={handleChange}
                                            placeholder="After N cards (0 = top)"
                                            className={styles.input}
                                        />
                                        <input
                                            type="number"
                                            min="0"
                                            name="feed_repeat"
                                            value={formData.feed_repeat}
                                            onChange={handleChange}
                                            placeholder="Repeat every N (blank = once)"
                                            className={styles.input}
                                        />
                                    </div>
                                    <span className={styles.hint}>Position = cards before the promo (0 = top). Repeat = re-insert every N cards; blank shows it once.</span>
                                </div>
                            )}

                            <CreativeSection
                                creativeMode={creativeMode}
                                setCreativeMode={setCreativeMode}
                                creativeHtml={formData.creative_html}
                                onHtmlChange={handleChange}
                                creativeItems={creativeItems}
                                uploading={uploading}
                                onImageUpload={handleImageUpload}
                                onVideoUpload={handleVideoUpload}
                                linkKind={linkKind}
                                setLinkKind={setLinkKind}
                                linkValue={linkValue}
                                setLinkValue={setLinkValue}
                                onSubmitLink={submitLink}
                                onOpenLibrary={openLibrary}
                                onSetItemRedirect={setItemRedirect}
                                onRemoveItem={removeItem}
                            />

                            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                <label className={styles.label}>Notes</label>
                                <textarea name="notes" value={formData.notes} onChange={handleChange} placeholder="Internal notes for this campaign..." className={styles.input} />
                            </div>
                        </div>

                        <div className={styles.actions}>
                            <Button variant="secondary" onClick={onCancel} type="button" disabled={loading}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" loading={loading} disabled={loading || !isDirty}>
                                {isEditing ? "Update Campaign" : "Save Campaign"}
                            </Button>
                        </div>
                    </form>
                )}
            </div>

            {libraryOpen && (
                <MediaLibraryPicker
                    library={library}
                    loading={libraryLoading}
                    creativeItems={creativeItems}
                    onPick={pickFromLibrary}
                    onRequestDelete={setAssetToDelete}
                    onClose={() => setLibraryOpen(false)}
                />
            )}

            {assetToDelete && (
                <DeleteAssetDialog
                    deleting={deletingAsset}
                    onCancel={() => setAssetToDelete(null)}
                    onConfirm={confirmDeleteAsset}
                />
            )}
        </div>
    );
};

export default AddLeadCampaign;
